import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from '@os/components/ui/button';
import { Card } from '@os/components/ui/card';
import { Input } from '@os/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@os/components/ui/dropdown-menu';
import { Folder, FileText, RefreshCw, BookOpen, BrainCircuit, ChevronDown } from 'lucide-react';
import { globalNavigator } from '../WindowContent';
import { getDocumentsPath } from '@/lib/osVFSBridge';
import { globalVFS } from '@os/utils/storage/VFS';
import { useToast } from '@os/components/ui/use-toast';

interface MapleAccessProps {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  filePath?: string; // NEW: optional chat folder path to auto-open
}

export const MapleAccess: React.FC<MapleAccessProps> = ({ onOpenWindow, filePath }) => {
  const [chatsRoot, setChatsRoot] = useState<string>('');
  const [chatFolders, setChatFolders] = useState<Array<{ path: string; name: string }>>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const LOG = (...args: any[]) => console.log('[MapleAccess]', ...args);

  // NEW: throttle duplicate open requests for the same convPath
  const lastOpenRef = React.useRef<{ path?: string; at?: number }>({});

  // NEW: announce readiness so the OS can safely send the open-target event
  useEffect(() => {
    LOG('ready: MapleAccess mounted, announcing readiness');
    // microtask: ensure listeners register cleanly
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('maple-access:ready'));
      LOG('ready: maple-access:ready dispatched');
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const loadChats = useCallback(async () => {
    setLoading(true);
    LOG('loadChats: syncing with VFS...');
    await globalNavigator.waitForInitialization();
    await globalNavigator.syncWithVFS();
    if (!chatsRoot) {
      LOG('loadChats: no chatsRoot yet.');
      setLoading(false);
      return;
    }
    LOG('loadChats: reading items in path:', chatsRoot);
    const items = globalNavigator.getItemsInPath(chatsRoot);
    LOG('loadChats: items found =', items.length);

    const folders = items
      .filter(i => i.type === 'folder')
      .map(i => ({ path: i.path, name: i.name }));

    LOG('loadChats: folders discovered =', folders.length);
    folders.sort((a, b) => a.name.localeCompare(b.name));
    setChatFolders(folders);
    setLoading(false);
  }, [chatsRoot]);

  useEffect(() => {
    const init = async () => {
      const docs = await getDocumentsPath();
      const root = `${docs}\\Chats`;
      LOG('init: docs path =', docs, ', chats root =', root);
      setChatsRoot(root);
      // Ensure root exists
      const exists = await globalVFS.exists(root);
      LOG('init: chats root exists?', exists);
      if (!exists) {
        LOG('init: creating chats root...');
        await globalVFS.createDirectory(root);
      }
      await loadChats();
    };
    init();
  }, [loadChats]);

  const filteredFolders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chatFolders;
    return chatFolders.filter(f => f.name.toLowerCase().includes(q));
  }, [chatFolders, search]);

  const openInMapleWord = async (folderPath: string, folderName: string) => {
    const convPath = `${folderPath}\\conversation.txt`;
    // Throttle: skip if same path requested within 1.5s
    const now = Date.now();
    if (lastOpenRef.current.path === convPath && lastOpenRef.current.at && (now - lastOpenRef.current.at) < 1500) {
      LOG('openInMapleWord: throttled duplicate open for', convPath);
      return;
    }
    lastOpenRef.current = { path: convPath, at: now };

    LOG('openInMapleWord: folder =', folderPath, ', convPath =', convPath);

    await globalNavigator.waitForInitialization();
    await globalNavigator.syncWithVFS();

    // NEW: read meta.json to extract UUIDs
    let chatId: string | undefined;
    let projectId: string | undefined;
    try {
      const metaPath = `${folderPath}\\meta.json`;
      const metaContent = await globalVFS.load(metaPath);
      LOG('openInMapleWord: meta.json length =', (metaContent || '').length);
      if (metaContent) {
        const meta = JSON.parse(metaContent);
        if (meta?.type === 'chat' && typeof meta?.id === 'string') {
          chatId = meta.id;
        }
        if (meta?.type === 'project' && typeof meta?.id === 'string') {
          projectId = meta.id;
        }
      }
    } catch (err) {
      LOG('openInMapleWord: meta.json read failed', err);
    }

    const exists = await globalVFS.exists(convPath);
    LOG('openInMapleWord: conversation.txt exists?', exists);

    let content = globalNavigator.findItemByPath(convPath)?.content;
    LOG('openInMapleWord: content from globalNavigator is', content !== undefined ? 'present' : 'undefined');

    if (content === undefined) {
      const loaded = (await globalVFS.load(convPath)) || '';
      LOG('openInMapleWord: content loaded from globalVFS length =', loaded.length);
      content = loaded;
    }

    if (!onOpenWindow) {
      LOG('openInMapleWord: onOpenWindow missing. Cannot open MapleWord.');
      toast({ title: 'Cannot open MapleWord', description: 'OS window open handler is not available.', variant: 'destructive' });
      return;
    }

    toast({ title: 'Opening MapleWord', description: `Loading ${folderName}/conversation.txt` });
    LOG('openInMapleWord: dispatching openWindow(maple-word) with UUIDs...', { chatId, projectId });
    // Pass UUIDs via a custom route string so WindowContent can forward as props
    const routeMarker = JSON.stringify({ chatId, projectId });
    window.dispatchEvent(new CustomEvent('operator:openWindow', { detail: { appId: 'maple-word', title: `MapleWord - ${folderName}`, filePath: convPath, fileContent: content || '', route: routeMarker } }));
  };

  const openInNotepad = async (folderPath: string, folderName: string) => {
    const convPath = `${folderPath}\\conversation.txt`;
    LOG('openInNotepad: folder =', folderPath, ', convPath =', convPath);

    await globalNavigator.waitForInitialization();
    await globalNavigator.syncWithVFS();

    let content = globalNavigator.findItemByPath(convPath)?.content;
    if (content === undefined) {
      content = (await globalVFS.load(convPath)) || '';
    }

    if (!onOpenWindow) {
      LOG('openInNotepad: onOpenWindow missing.');
      toast({ title: 'Cannot open Notepad', description: 'OS window open handler is not available.', variant: 'destructive' });
      return;
    }

    toast({ title: 'Opening Notepad', description: `Loading ${folderName}/conversation.txt` });
    onOpenWindow('notepad', `Notepad - ${folderName}`, convPath, content || '');
  };

  const openFolder = (folderPath: string, folderName: string) => {
    LOG('openFolder: folder =', folderPath);
    if (!onOpenWindow) {
      LOG('openFolder: onOpenWindow missing.');
      toast({ title: 'Cannot open folder', description: 'OS window open handler is not available.', variant: 'destructive' });
      return;
    }
    onOpenWindow('file-explorer', `Chats - ${folderName}`, folderPath);
  };

  // NEW: Also respond to external 'maple-access:open-target' events (supports focusing existing window)
  useEffect(() => {
    const handler = async (e: Event) => {
      const { filePath: target } = (e as CustomEvent<{ filePath?: string }>).detail || {};
      LOG('event: maple-access:open-target received, target =', target);
      if (!target || !onOpenWindow) {
        LOG('event: maple-access:open-target abort (missing target or onOpenWindow).');
        return;
      }
      const folderName = target.split('\\').pop() || 'Chat';
      LOG('event: invoking openInMapleWord for', folderName);
      await openInMapleWord(target, folderName);
    };
    window.addEventListener('maple-access:open-target', handler as EventListener);
    return () => {
      window.removeEventListener('maple-access:open-target', handler as EventListener);
    };
  }, [onOpenWindow]);

  // NEW: If a specific chat folder path is provided, auto-open MapleWord for it
  useEffect(() => {
    const autoOpen = async () => {
      LOG('autoOpen: filePath prop =', filePath);
      if (!filePath || !onOpenWindow) {
        if (!filePath) LOG('autoOpen: no filePath provided.');
        if (!onOpenWindow) LOG('autoOpen: onOpenWindow missing.');
        return;
      }
      const folderName = filePath.split('\\').pop() || 'Chat';
      LOG('autoOpen: calling openInMapleWord for', folderName);
      await openInMapleWord(filePath, folderName);
    };
    autoOpen();
    // Only auto-open when filePath is initially provided
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header / Actions */}
      <div className="border-b border-border p-3 bg-muted/50 flex items-center gap-2">
        <BrainCircuit className="w-5 h-5 text-blue-600" />
        <h1 className="text-sm font-semibold">Access Your AI Chats</h1>
        <div className="ml-auto flex items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search chats..."
            className="h-8 w-48"
          />
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={async () => {
              LOG('UI: Refresh clicked.');
              await globalNavigator.syncWithVFS();
              await loadChats();
            }}
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading chats...</div>
        ) : filteredFolders.length === 0 ? (
          <div className="text-sm text-muted-foreground">No chats found in {chatsRoot}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredFolders.map((f) => (
              <Card key={f.path} className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Folder className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium truncate">{f.name}</span>
                </div>
                <div className="flex gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="default" size="sm" className="gap-1">
                        <FileText className="w-4 h-4" />
                        Open
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => { LOG('UI: Open → MapleWord clicked for', f.path); openInMapleWord(f.path, f.name); }}>
                        <FileText className="w-4 h-4 mr-2" />
                        Open in MapleWord
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { LOG('UI: Open → Notepad clicked for', f.path); openInNotepad(f.path, f.name); }}>
                        <FileText className="w-4 h-4 mr-2" />
                        Open in Notepad
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="outline" size="sm" onClick={() => { LOG('UI: Folder clicked for', f.path); openFolder(f.path, f.name); }} className="gap-1">
                    <Folder className="w-4 h-4" />
                    Folder
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="border-t border-border p-2 bg-muted/50 text-xs text-muted-foreground flex justify-between">
        <span>{filteredFolders.length} chat(s)</span>
        <span>Path: {chatsRoot}</span>
      </div>
    </div>
  );
};

export default MapleAccess;